/**
 * Shared rich Shop product row used by desktop Menu and mobile accordion.
 *
 * Keeps icon + label + descriptor + optional New badge identical across
 * PublicNav surfaces so the two code paths cannot drift (PBI #1063).
 */

import { Badge, Group, Stack, Text, ThemeIcon, rem } from '@mantine/core';
import {
  SHOP_NAV_HIGHLIGHT,
  isShopNavHighlighted,
  type ShopNavItem,
} from '../../constants/shopNav';

interface ShopNavItemContentProps {
  item: ShopNavItem;
  /** When true, use denser spacing for footer / compact rows. */
  compact?: boolean;
}

/**
 * Renders one Shop product's rich content (icon, label, descriptor, New badge).
 */
export const ShopNavItemContent = ({ item, compact = false }: ShopNavItemContentProps) => {
  const Icon = item.icon;
  const showNew = isShopNavHighlighted(item.key);

  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon
        size={compact ? 28 : 36}
        radius="md"
        variant="light"
        color={item.color}
        mt={compact ? 0 : rem(2)}
      >
        <Icon size={compact ? 16 : 20} />
      </ThemeIcon>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size={compact ? 'sm' : 'sm'} fw={600} style={{ lineHeight: 1.3 }}>
            {item.label}
          </Text>
          {showNew && (
            <Badge size="xs" variant="filled" color={item.color} radius="sm">
              {SHOP_NAV_HIGHLIGHT.badgeLabel}
            </Badge>
          )}
        </Group>
        {!compact && (
          <Text size="xs" c="dimmed" style={{ lineHeight: 1.35 }}>
            {item.descriptor}
          </Text>
        )}
      </Stack>
    </Group>
  );
};
